'use client';

import PostMessage from '@components/PostMessage';
import Typography from '@ui/Typography';
import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { Rating } from '@mui/material';
import CommunityReviewsDialog from '@/components/next-page-components/CommunityReviewsDialog/CommunityReviewsDialog';
import { useGetCommunityRating } from '@/actions/nonprofit.actions';
import Chip from '@ui/Chip';
import ClickableTooltipWrapper from '@/components/Styled/ClickableTooltipWrapper';
import Image from 'next/image';
import { formatNumberForLocale } from '@/utils/stringHelpers';
import AppLink from '@/components/next-ui/Link/Link';

function ProfileTitle(props) {
  const { nonProfit, slug, addReview } = props;

  const {
    data: nonProfitRating,
    isLoading: isnonProfitRatingLoading,
    refetch: refetchNonProfitRating,
  } = useGetCommunityRating(nonProfit.id);

  const { totalRating = 0, reviewCount = 0 } =
    nonProfitRating || nonProfit.rating || {};

  const [showReviews, setShowReviews] = useState(!!addReview);

  const averageRating =
    reviewCount > 0 ? Math.round((totalRating / reviewCount) * 10) / 10 : 0;
  const npoLocation = [nonProfit.city, nonProfit.state, nonProfit.country]
    .filter((i) => !!i)
    .join(', ');

  const handleReviewClick = () => {
    setShowReviews(true);
  };

  const renderReview = () => {
    return (
      <Typography
        variant="subtitle1"
        color="primary"
        className="cursor-pointer hover:underline"
        onClick={handleReviewClick}>
        {reviewCount
          ? `${reviewCount} Neki review${reviewCount !== 1 ? 's' : ''}`
          : ''}
      </Typography>
    );
  };

  return (
    <div>
      <div className="flex w-full flex-nowrap items-center justify-between gap-3 lg:justify-start lg:pr-2">
        <Typography variant="h2" component="h2">
          {nonProfit.name}
        </Typography>
      </div>
      <div className="my-1 -ml-1.5 flex flex-row">
        {nonProfit?.isClaimed && (
          <AppLink href={'https://help.neki.io/en/article/what-is-neki-score'} target="__blank" passHref>
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <Image
                  src="/assets/images/neki-score.png"
                  alt="neki-score"
                  width={48}
                  height={48}
                  className="rounded-full"
                />

                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <Typography className="text-16 font-medium">
                      Neki Score:{' '}
                      {formatNumberForLocale(nonProfit?.company?.points)}
                    </Typography>
                  </div>

                  <Typography className="text-11 italic text-gray-500 sm:text-12">
                    Measure of a community’s collective impact on Neki
                  </Typography>
                </div>
              </div>
            </div>
          </AppLink>
        )}
      </div>
      <div className="my-1 flex flex-row gap-2">
        {averageRating > 0 && (
          <Typography variant="subtitle1" color="textSecondary">
            {averageRating.toFixed(1)}
          </Typography>
        )}

        <div
          onClick={handleReviewClick}
          className="hover:cursor-pointer"
          title="Write a review">
          <Rating
            name="read-only"
            value={averageRating}
            precision={0.1}
            readOnly
            className="text-primary"
          />
        </div>

        {renderReview()}
        {showReviews && (
          <CommunityReviewsDialog
            slug={slug}
            nonProfit={nonProfit}
            onClose={() => setShowReviews(false)}
            refetchNonProfitRating={refetchNonProfitRating}
            communityRating={nonProfitRating || nonProfit.rating}
          />
        )}
      </div>

      <Typography variant="subtitle2" color="textSecondary">
        {npoLocation}
      </Typography>

      <div className="my-1 flex flex-row items-center gap-2">
        {nonProfit?.numFollowers > 0 && (
          <Typography variant="subtitle2" color="textSecondary">
            {nonProfit.numFollowers} follower
            {nonProfit?.numFollowers > 1 ? 's' : ''}
          </Typography>
        )}
      </div>

      <div className="inline-block w-fit">
        <PostMessage message={nonProfit.description} lineClamp={1} />
      </div>
      {nonProfit.giving_fund_status === 'ON_HOLD' && (
        <Typography variant="subtitle2" className="text-red-600">
          This organization is currently not in compliance with California
          regulations - please check back later for updates on their status.
        </Typography>
      )}
      {nonProfit.website_url && (
        <div className="flex items-center gap-2">
          <Typography
            variant="hyperlink"
            component="a"
            target="_blank"
            className="hover:underline"
            href={nonProfit.website_url}>
            {nonProfit.website_url}
          </Typography>
        </div>
      )}

      {!isEmpty(nonProfit.ein) && (
        <div className="my-1 flex items-center gap-2">
          <Typography variant="subtitle2" color="textSecondary">
            {nonProfit.country === 'IN' ? 'Pan #:' : 'EIN:'}
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            {nonProfit.ein}
          </Typography>
        </div>
      )}

      {!isEmpty(nonProfit.certifications) && (
        <div className="my-2 flex flex-wrap items-center gap-2">
          {nonProfit.certifications.map((c) => (
            <div key={c.name}>
              {c.description?.length ? (
                <ClickableTooltipWrapper
                  message={c.description}
                  placement="top">
                  <Chip
                    className="cursor-pointer"
                    key={c.name}
                    color="secondary"
                    text="small">
                    {c.name}
                  </Chip>
                </ClickableTooltipWrapper>
              ) : (
                <Chip key={c.name} color="secondary" text="small">
                  {c.name}
                </Chip>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProfileTitle;
